import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { ActivatedRoute, ActivatedRouteSnapshot, CanActivate, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { Observable, Subject } from 'rxjs';
import { AppStateService, HttpService, EnvService, UtilsService, USER_MGMT_CNST, PROJECT_CONSTANTS, TokenService, CsrfTokenService, DataService, AuthenticationService } from '../../../../../../usermgmt/src/public-api';
import { isNullOrUndefined } from '../../../../../../usermgmt/src/lib/shared/common-utils';
import { KeycloakService } from '../../../../../../usermgmt/src/lib/services/keycloak.service';
import { isPlatformBrowser } from '@angular/common';
import { MessageService } from 'primeng/api';
import { PermissionsService } from '../../../services/permissions.service';


@Injectable({
  providedIn: 'root'
})
export class ApplicationGuard implements CanActivate {
  userPermissions: any;
  private isBrowser: boolean;

  constructor(
    private authSrv: AuthenticationService,
    private keycloak: KeycloakService,
    private router: Router,
    private appStateSrv: AppStateService,
    private httpSrv: HttpService,
    private envService: EnvService,
    // private utilService: UtilsService,
    private messageService: MessageService,
    private tokenService: TokenService,
    private csrfTokenSrv: CsrfTokenService,
    private dataSrv: DataService,
    private permissionsService: PermissionsService,
    @Inject(PLATFORM_ID) private platformId: object
  ) {
    this.isBrowser = isPlatformBrowser(this.platformId);
  }

  canActivate(
    next: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
    return this.checkAccess(next, state);
  }

  private async checkAccess(
    next: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Promise<boolean | UrlTree> {
    const resource = next.data['resource'] || '';
    const loggedIn = this.authSrv.isLoggedIn() || this.keycloak.isLoggedIn();

    if (!loggedIn) {
      window.location.replace('/landing');
      return false;
    }

    // const accessToken = await this.authSrv.getAccessToken();
    const accessToken = await this.keycloak.getToken();
    localStorage.setItem('token', accessToken);

    if (!accessToken || this.tokenService.isTokenExpired(accessToken)) {
      return this.handleUnAuthorizedAccess("Session expired. Please log in again.", ['username']);
    }

    const tokenValidated = await this.validateCsrfToken(accessToken);

    if (!tokenValidated) {
      return this.handleUnAuthorizedAccess("Please login to view requested page.", ['username']);
    }
    let userId;
    if (this.keycloak.isLoggedIn()) {
      const userProfile = await this.appStateSrv.getUserInfo();
      userId = userProfile.id;
    } else {
      userId = this.appStateSrv.getUserInfoFromLocalStorage()?.userId;
    }

    if (!userId) {
      this.keycloak.logout();
      return false;
    }

    const hasPermissionsLoaded = this.appStateSrv.getPermissionsLoaded();
    this.userPermissions = this.appStateSrv.getUserPermissions();
    this.permissionsService.setPermissions(this.userPermissions);

    if (hasPermissionsLoaded) {
      return this.handlePermissionsLoaded(resource);
    } else {
      return this.loadUserPermissions(resource);
    }
  }

  private handlePermissionsLoaded(resource: string): boolean | UrlTree {
    const applicationName = PROJECT_CONSTANTS.APPLICATION_NAME;
    const usermgmtAppName = PROJECT_CONSTANTS.USERMGMT_APP_NAME;

    if (this.userPermissions && Object.keys(this.userPermissions).length > 0 && Object.keys(this.userPermissions).includes(applicationName)) {
      return this.checkPermissions(resource);
    } else if (this.appStateSrv.getLoggedInUserType() === USER_MGMT_CNST.LOGGED_IN_USER_TYPES.organizationUser) {
      return true;
    } else if (Object.keys(this.userPermissions).includes(usermgmtAppName)) {
      if (resource === usermgmtAppName) {
        return true;
      } else {
        this.router.navigate(['usermgmt', 'applications']);
        return false;
      }
    }

    this.router.navigate(['landing']);
    return false;
  }

  private async validateCsrfToken(accessToken: string): Promise<boolean> {
    if (!isNullOrUndefined(accessToken)) {
      if (!this.csrfTokenSrv.doesCsrfTokenExist()) {
        const response = await this.csrfTokenSrv.generateCsrfToken(accessToken);
        if (Object.keys(response).length === 0) {
          return false;
        }
        this.setCsrfToken(response);
        const customerDetails = {
          customerId: response['response']['userDetails']['customerId'],
          customerName: response['response']['userDetails']['customerName']
        };
        if (this.isBrowser) {
          localStorage.setItem('customerInfo', JSON.stringify(customerDetails));
        }
        this.dataSrv.setLoggedInUserInfo(response);
      }
      return true;
    }
    return false;
  }

  private setCsrfToken(csrfTokenResponse: any): void {
    const { response } = csrfTokenResponse;
    if (!isNullOrUndefined(response)) {
      const { tokens } = response;
      if (!isNullOrUndefined(tokens)) {
        const { csrf } = tokens;
        if (isNullOrUndefined(localStorage.getItem('csrfToken')) && this.isBrowser) {
          localStorage.setItem('csrfToken', csrf);
        }
      }
    }
  }

  private loadUserPermissions(resource: string): Promise<boolean | UrlTree> {
    this.appStateSrv.setInProcess(true);

    return new Promise((resolve, reject) => {
      this.httpSrv.makeGetApiCall('GET_USER_PERMISSIONS', this.envService.baseUrl).subscribe({
        next: (res: any) => {
          resolve(this.handleUserPermissionsResponse(res, resource));
        },
        error: () => {
          resolve(this.handleUnAuthorizedAccess());
        }
      });
    });
  }

  private handleUserPermissionsResponse(res: any, resource: string): boolean | UrlTree {
    res.response = res?.response ? res.response : res;
    if (!isNullOrUndefined(res.response)) {
      this.userPermissions = res.response?.permissions ? res.response.permissions : {};
      const metaInfo = res.response?.meta ? res.response.meta : {};
      this.permissionsService.setPermissions(this.userPermissions);
      this.appStateSrv.setLoggedInUserType(res.response);
      this.appStateSrv.setUserMetaInfo(metaInfo);
      this.appStateSrv.setInProcess(false);
      this.appStateSrv.setPermissionsLoaded(true);
      this.appStateSrv.setUserPermissionsRef(this.userPermissions);

      localStorage.setItem('userPermissions', JSON.stringify(this.userPermissions));
      localStorage.setItem('metaInfo', JSON.stringify(metaInfo));


      if (this.userPermissions && Object.keys(this.userPermissions).length > 0 && Object.keys(this.userPermissions).includes(PROJECT_CONSTANTS.APPLICATION_NAME)) {
        return this.checkPermissions(resource) ? true : this.handleUnAuthorizedAccess();
      } else if (Object.keys(this.userPermissions).includes(PROJECT_CONSTANTS.USERMGMT_APP_NAME)) {
        if (resource === "Home") {
          return true;
        } else {
          this.router.navigate(['usermgmt', 'applications']);
          return this.handleUnAuthorizedAccess("You don't have permissions to view the requested Application", ['usermgmt', 'applications']);
        }
      } else {
        return this.handleUnAuthorizedAccess();
      }
    } else {
      return this.handleUnAuthorizedAccess();
    }
  }

  handleUnAuthorizedAccess(message: string = "You don't have permissions to view the requested resource", route: string[] = ['', 'login']) {
    this.messageService.add({
      severity: 'error',
      summary: 'Error',
      detail: message
    });
    this.router.navigate(['', 'access-denied']);
    return false;
  }

  checkPermissions(resource: string): boolean {
    const userType = this.appStateSrv.getLoggedInUserType();
    if (resource == PROJECT_CONSTANTS.USERMGMT_APP_NAME) {
      return true;
    }
    if (resource) {
      const appName = PROJECT_CONSTANTS.APPLICATION_NAME;
      if (resource === 'Home') {
        if (this.userPermissions[appName]?.resources?.[resource]) {
          return true;
        } else {
          this.router.navigate(['', 'access-denied']);
          return false;
        }
      } else if (this.userPermissions?.[appName]?.resources?.[resource]) {
        return true;
      }
    }
    return false;
  }
}
